"use client";

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";
import "./ProfilDoctor.css";

import MedecinInformation from "../../componants/ProfileMedecinPage/MedecinInformation/MedecinInformation";
import DoctorProfileTabsDoctor from "../../componants/ProfileMedecinPage/DoctorProfileTabsDoctor/DoctorProfileTabsDoctor";
import DoctorPracticePanel from "../../componants/ProfileMedecinPage/DoctorPracticePanel/DoctorPracticePanel";
import DoctorPostsPanel from "../../componants/ProfileMedecinPage/DoctorPostsPanel/DoctorPostsPanel";
import DoctorFollowersPanel from "../../componants/ProfileMedecinPage/DoctorFollowersPanel/DoctorFollowersPanel";
import DoctorSavedPanel from "../../componants/ProfileMedecinPage/DoctorSavedPanel/DoctorSavedPanel";

import DoctorTodaySidePanel from "../../componants/ProfileMedecinPage/DoctorTodaySidePanel/DoctorTodaySidePanel";

import { getCurrentDoctor, updateDoctorYearsOfExperience } from "../../../authDoctor/redux/thunks/doctorsThunks";

export default function ProfilDoctor() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState("info");

  const { user: currentUser, loading: userLoading, error: userError } =
    useSelector((state) => state.doctor || {});

  const doctorId = currentUser?.id || currentUser?._id || currentUser?.userId;

  // ✅ FIX PRINCIPAL : toujours refetch au montage du composant
  // Ne pas vérifier si currentUser existe — on veut TOUJOURS les données fraîches
  useEffect(() => {
    dispatch(getCurrentDoctor());
  }, [dispatch]); // ✅ dépendance vide = une seule fois au montage

  const handleSaveYearsOfExperience = useCallback(
    async (yearsValue) => {
      if (!doctorId) throw new Error("No doctor ID");
      await dispatch(
        updateDoctorYearsOfExperience({
          id: doctorId,
          yearsOfExperience: yearsValue,
        })
      ).unwrap();
    },
    [dispatch, doctorId]
  );

  const handleReloadDoctor = useCallback(async () => {
    await dispatch(getCurrentDoctor()).unwrap();
  }, [dispatch]);

  const doctorDisplay = useMemo(() => {
    return {
      id: currentUser?.id || currentUser?._id || currentUser?.userId,
      fullName: currentUser?.fullName || currentUser?.name || "Dr. —",
      specialty: currentUser?.specialty || "—",
      email: currentUser?.email || "—",
      phone: currentUser?.phone || "—",
      avatarUrl:
        currentUser?.avatarUrl ||
        currentUser?.avatar ||
        "https://i.pravatar.cc/120?img=5",
      gender: currentUser?.gender || "not_specified",
      about: currentUser?.about || "",
      yearsOfExperience:
        currentUser?.yearsOfExperience ??
        currentUser?.experienceYears ??
        currentUser?.experience,
      raw: currentUser,
    };
  }, [currentUser]);

  const handleEditProfile = () => navigate("/profile/edit");

  // ✅ Loading initial (pas encore de user)
  if (!currentUser && userLoading) {
    return (
      <AppLayoutDoctor>
        <div className="ddp-page">Loading doctor profile...</div>
      </AppLayoutDoctor>
    );
  }

  if (!currentUser && userError) {
    return (
      <AppLayoutDoctor>
        <div className="ddp-page">
          <p style={{ color: "crimson" }}>{String(userError)}</p>
          <button className="ddp-retry" onClick={() => dispatch(getCurrentDoctor())} type="button">
            {t("doctorFeed.retry")}
          </button>
        </div>
      </AppLayoutDoctor>
    );
  }

  if (!currentUser) {
    return (
      <AppLayoutDoctor>
        <div className="ddp-page">
          <p>⚠️ Session expirée. Veuillez vous reconnecter.</p>
          <button className="ddp-retry" onClick={() => (window.location.href = "/login")} type="button">
            {t("doctorFeed.signIn")}
          </button>
        </div>
      </AppLayoutDoctor>
    );
  }

  return (
    <AppLayoutDoctor>
      <div className="ddp-page">
        <div className="ddp-headerWrap">
          <MedecinInformation doctor={doctorDisplay} onEdit={handleEditProfile} variant="header" />
        </div>

        <DoctorProfileTabsDoctor active={activeTab} onChange={setActiveTab} />

        <div className="ddp-content">
          {activeTab === "info" && (
            <div className="ddp-infoGrid">
              <div className="ddp-leftCol">
                <DoctorPracticePanel
                  doctor={doctorDisplay.raw || doctorDisplay}
                  onSaveYearsOfExperience={handleSaveYearsOfExperience}
                  onReloadDoctor={handleReloadDoctor}
                />
              </div>
              <aside className="ddp-rightCol">
                <DoctorTodaySidePanel doctorId={doctorId} />
              </aside>
            </div>
          )}

          {activeTab === "posts" && (
            <div className="ddp-infoGrid">
              <div className="ddp-leftCol">
                <DoctorPostsPanel doctorId={doctorId} doctorDisplay={doctorDisplay} />
              </div>
              <aside className="ddp-rightCol">
                <DoctorTodaySidePanel doctorId={doctorId} />
              </aside>
            </div>
          )}

          {activeTab === "followers" && (
            <div className="ddp-infoGrid">
              <div className="ddp-leftCol">
                <DoctorFollowersPanel
                  doctorId={doctorId}
                  profileId={doctorId}
                  profileType="doctor"
                />
              </div>
              <aside className="ddp-rightCol">
                <DoctorTodaySidePanel doctorId={doctorId} />
              </aside>
            </div>
          )}

          {activeTab === "saved" && (
            <div className="ddp-infoGrid">
              <div className="ddp-leftCol">
                <DoctorSavedPanel doctorDisplay={doctorDisplay} />
              </div>
              <aside className="ddp-rightCol">
                <DoctorTodaySidePanel doctorId={doctorId} />
              </aside>
            </div>
          )}
        </div>
      </div>
    </AppLayoutDoctor>
  );
}